import type { JSX } from "react";

import { ChevronDown } from "lucide-react";

import type { RecommendationFilterOption } from "../hooks/useRecommendationFilters";

interface RecommendationFilterSelectProps {
  /** Text rendered above the control. */
  label: string;
  /** Currently selected option value. */
  value: string;
  /** Options to offer, in display order. */
  options: RecommendationFilterOption[];
  /** Called with the newly selected option value. */
  onChange: (value: string) => void;
}

/**
 * One labelled dropdown in the Recommendations filter bar.
 *
 * Native <select> rather than react-select: these are compact, single-value filters with no search
 * or multi-select — five on the AI Audit tab, three on the Persona Recommendations tab — so the
 * native control is lighter and gets keyboard and mobile behaviour for free. Styling follows the
 * shared input pattern in BusinessProfileDialog, scaled down for a filter bar.
 *
 * The option shape is owned by `useRecommendationFilters`, which is what attaches the counts.
 *
 * @example
 *   <RecommendationFilterSelect
 *     label="Status"
 *     value={filters.status}
 *     options={[{ value: "all", label: "All Statuses", count: 12 }]}
 *     onChange={(value) => setFilter("status", value)}
 *   />;
 */
const RecommendationFilterSelect = ({
  label,
  value,
  options,
  onChange,
}: RecommendationFilterSelectProps): JSX.Element => (
  <label className="flex flex-col gap-1">
    <span className="text-xs font-semibold text-on-surface-variant">{label}</span>
    <div className="relative">
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full appearance-none bg-surface-container-low border-none rounded-xl pl-4 pr-10 py-2 text-sm font-semibold text-on-surface cursor-pointer outline-none transition-all focus:ring-2 focus:ring-primary focus:bg-surface-container-lowest"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label} ({option.count})
          </option>
        ))}
      </select>
      <ChevronDown className="w-4 h-4 text-outline pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
    </div>
  </label>
);

export default RecommendationFilterSelect;
